import { clearSvgTextFill, readSvgTextFill, setSvgTextFill } from '../../lib/bubble/svgTextFill';
import { infraBubbleStyles as s } from './bubbleDimColors';

/**
 * Dimming for text, icons and volume labels — the `<text>` fill / size-arrow `<path>` fill /
 * icon `opacity` trio that both volume labels and infra chips reuse.
 *
 * Split out of `bubbleEmphasis.ts`; `chipEmphasis` behaviour in that file imports the text and
 * icon helpers from here rather than duplicating them.
 */

export const volLabelSelector = '.b-vol-label';

export function forEachSizeArrowPath(container: Element, fn: (path: SVGPathElement) => void) {
  container.querySelectorAll('.b-vol-size-arrow path').forEach((path) => {
    fn(path as SVGPathElement);
  });
}

export function saveAndDimTextFill(container: Element) {
  container.querySelectorAll('text').forEach((text) => {
    const el = text as SVGTextElement;
    el.dataset.sbDimTextFill = readSvgTextFill(el);
    setSvgTextFill(el, s.chip.text.dim);
  });
  forEachSizeArrowPath(container, (el) => {
    el.dataset.sbDimPathFill = el.getAttribute('fill') ?? '';
    el.setAttribute('fill', s.chip.text.dim);
  });
}

export function reapplyDimmedTextFill(container: Element) {
  container.querySelectorAll('text').forEach((text) => {
    setSvgTextFill(text as SVGTextElement, s.chip.text.dim);
  });
  forEachSizeArrowPath(container, (el) => {
    el.setAttribute('fill', s.chip.text.dim);
  });
}

export function restoreDimmedTextFill(container: Element) {
  container.querySelectorAll('text').forEach((text) => {
    const el = text as SVGTextElement;
    const fill = el.dataset.sbDimTextFill;
    if (fill != null) {
      if (fill === '') clearSvgTextFill(el);
      else setSvgTextFill(el, fill);
    }
    delete el.dataset.sbDimTextFill;
  });
  forEachSizeArrowPath(container, (el) => {
    const fill = el.dataset.sbDimPathFill;
    if (fill != null) {
      if (fill === '') el.removeAttribute('fill');
      else el.setAttribute('fill', fill);
    }
    delete el.dataset.sbDimPathFill;
  });
}

export function saveAndDimIconOpacity(
  icon: SVGElement,
  stateHost: { dataset: DOMStringMap },
  dimOpacity: string
) {
  stateHost.dataset.sbDimIconOpacity = icon.getAttribute('opacity') ?? '';
  icon.setAttribute('opacity', dimOpacity);
}

export function reapplyDimmedIconOpacity(icon: SVGElement, dimOpacity: string) {
  icon.setAttribute('opacity', dimOpacity);
}

export function restoreDimmedIconOpacity(icon: SVGElement, stateHost: { dataset: DOMStringMap }) {
  const iconOpacity = stateHost.dataset.sbDimIconOpacity;
  if (iconOpacity != null) {
    if (iconOpacity === '') icon.removeAttribute('opacity');
    else icon.setAttribute('opacity', iconOpacity);
  }
  delete stateHost.dataset.sbDimIconOpacity;
}

export function restoreVolumeLabels(container: HTMLElement) {
  container.querySelectorAll<SVGElement>(volLabelSelector).forEach(restoreDimmedVolumeLabel);
}

export function applyDimToVolumeLabel(label: SVGElement) {
  if (label.dataset.sbDim === '1' && label.querySelector('text')?.dataset.sbDimTextFill != null) {
    reapplyVolumeLabelDimVisuals(label);
    return;
  }
  label.dataset.sbDim = '1';
  saveAndDimTextFill(label);
  const icon = label.querySelector<SVGElement>('.b-vol-icon svg');
  if (icon) saveAndDimIconOpacity(icon, label, s.icon.dimOpacity);
}

export function reapplyVolumeLabelDimVisuals(label: SVGElement) {
  reapplyDimmedTextFill(label);
  const icon = label.querySelector<SVGElement>('.b-vol-icon svg');
  if (icon) reapplyDimmedIconOpacity(icon, s.icon.dimOpacity);
}

export function restoreDimmedVolumeLabel(label: SVGElement) {
  if (label.dataset.sbDim !== '1') return;
  restoreDimmedTextFill(label);
  const icon = label.querySelector<SVGElement>('.b-vol-icon svg');
  if (icon) restoreDimmedIconOpacity(icon, label);
  delete label.dataset.sbDim;
}

/** Re-applies volume label dim attrs after zoom re-layout resets text fill. */
export function reapplyVolumeLabelDimIfNeeded(label: SVGElement): void {
  if (label.dataset.sbDim !== '1') return;
  applyDimToVolumeLabel(label);
}

